/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import {
  Segment,
  Transition,
  Container,
  Dimmer,
  Loader,
  Grid,
  Button,
  Icon,
  Accordion,
  Table,
  Form,
  Dropdown,
  DropdownProps,
  Modal,
  Popup,
} from "semantic-ui-react";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import {
  StripeSubscriptionQuery,
  StripeSubscription,
  updateStripePaymentCardMutation,
  StripeInvoice,
  GetInvoicesQuery,
  InvoiceData,
  GetUsageQuery,
} from "../../../graphql/subscription";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import {
  authCheckCallApi,
  getPlanInfo,
  parseGraphQlError,
  DateFormatLong,
  isMonitoringPlan,
} from "../../Authenticator/utils";
import { AuthProps } from "aws-amplify-react/props";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { ChangeSubscriptionDialog } from ".";
import {
  OrganizationQuery,
  OrganizationData,
  UpdateVatMutation,
} from "../../../graphql/organization";
import useForm from "react-hook-form";
import { vatOptions } from "./VatTypes";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import PaymentMethod, {
  createPaymentMethod,
} from "../../PaymentMethod/PaymentMethod";
import { PaymentMethodCreateParams } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

const Payment: React.FC<AuthProps> = ({ authData }) => {
  type VatFormData = {
    vatNumber: string;
    vatType: string;
  };
  const { register, handleSubmit, errors, setValue } = useForm<VatFormData>();
  const [vatNumber, setVatNumber] = useState<string>("");
  const [vatError, setVatError] = useState<boolean>(false);
  const [vatType, setVatType] = useState<string>("");
  const [enableRemoveVat, setEnableRemoveVat] = useState<boolean>(false);
  const [updatePaymentMethod, setUpdatePaymentMethod] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation(["billing", "common"]);

  const handleVatNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setVatError(false);
    const { value } = event.target;
    setVatNumber(value);
  };
  const handleVatTypeChange = (_: any, data: DropdownProps) => {
    setVatError(false);
    setValue(data.name, data.value);
    setVatType(data.value as string);
  };

  const stripe = useStripe();
  const elements = useElements();
  const { plan: currentPlan } = authData.signInUserSession.idToken.payload;
  const { plan, orgId, planExpires } =
    authData.signInUserSession.idToken.payload;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [showVat, setShowVat] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [changeSubscription, setChangeSubscription] = useState<string | null>(
    null
  );
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [message, setMessage] = useState<MessageDialogParams | null>(null);

  // Note: in this file and elsewhere, a skip graphQl query work around is implemented,
  // in order to avoid repeating calls to onCompleted.
  // For more info see here: https://github.com/apollographql/react-apollo/issues/3505

  const [skipSubUpdate, setSkipSubUpdate] = useState(false);
  const [
    getStripeSubscription,
    {
      loading: loadingPaymentMethod,
      data: subscriptionData,
      error: queryError,
    },
  ] = useLazyQuery<StripeSubscription>(StripeSubscriptionQuery, {
    fetchPolicy: "network-only",
    variables: {},
    onError: () => {
      setPaymentMethod(getPaymentMethodDescription(true));
    },
    onCompleted: (data: any) => {
      if (!skipSubUpdate) {
        if (queryError || !data) {
          return setPaymentMethod(getPaymentMethodDescription(true));
        }

        setPaymentMethod(getPaymentMethodDescription());

        handleLatestInvoiceRequiredAction(data);

        setSkipSubUpdate(true);
      }
    },
  });

  const [skipOrgUpdate, setSkipOrgUpdate] = useState(false);
  const [getOrg, { loading: loadingOrgInfo }] = useLazyQuery<OrganizationData>(
    OrganizationQuery,
    {
      fetchPolicy: "cache-and-network",
      onCompleted: (data: any) => {
        if (!skipOrgUpdate && data) {
          setStripeEnabled(
            data.organization.plan && !data.organization.planInfo.stripeDisabled
          );
          const vatNumber = data.organization.stripeTaxId
            ? data.organization.stripeTaxId.value
            : "";
          const vatType = data.organization.stripeTaxId
            ? data.organization.stripeTaxId.type
            : "";
          setVatNumber(vatNumber);
          setValue("vatNumber", vatNumber);
          setVatType(vatType);
          setValue("vatType", vatType);
          setEnableRemoveVat(vatNumber.length);
          setSkipOrgUpdate(true);
        }
      },
    }
  );

  const [getInvoices, { loading: loadingInvoices, data: invoiceData }] =
    useLazyQuery<InvoiceData>(GetInvoicesQuery, {
      variables: { orgId: orgId },
      onError: () => {
        !message &&
          setMessage({
            title: t("invoices"),
            message: t("invoice_error"),
          });
      },
    });

  const [getUsage, { data: usageData }] = useLazyQuery<any>(GetUsageQuery, {
    variables: { orgId: orgId },
  });

  useEffect(() => {
    authCheckCallApi(getStripeSubscription);
    authCheckCallApi(getOrg);
    authCheckCallApi(getInvoices);

    if (currentPlan !== "free") {
      authCheckCallApi(getUsage);
    }
  }, [getStripeSubscription, getOrg, getInvoices, getUsage, currentPlan]);

  useEffect(() => {
    register({ name: "vatType" }, { required: true });
  }, [register]);

  const planInfo = getPlanInfo(plan);

  const isPlanExpires =
    planExpires && new Date(parseInt(planExpires)) > new Date();

  const enableSubscriptionChange =
    plan &&
    plan !== "expired" &&
    plan !== "free" &&
    stripeEnabled &&
    !isPlanExpires;

  const [updateVatNumber, { loading: updateVatLoading }] = useMutation(
    UpdateVatMutation,
    {
      onError: (error) => {
        if (!message) {
          let messageText = t("set_vat_error");
          if (error.message.includes("Invalid value")) {
            messageText = t("vat_value_error");
          } else if (error.message.includes("value and type already exists")) {
            messageText = t("vat_exists_error");
          }

          setMessage({
            title: t("update_vat"),
            message: messageText,
          });
        }
        setVatError(true);
      },
      onCompleted: (_: any) => {
        setMessage({
          title: t("update_vat"),
          message: t("set_vat_ok"),
        });
        setTimeout(() => {
          setSkipOrgUpdate(false);
          authCheckCallApi(getOrg);
        }, 0);
      },
    }
  );

  const handleLatestInvoiceRequiredAction = (data: StripeSubscription) => {
    if (
      data.stripeSubscription &&
      data.stripeSubscription.latestInvoice &&
      data.stripeSubscription.latestInvoice.paymentIntent
    ) {
      const { paymentIntent } = data.stripeSubscription.latestInvoice;
      if (
        stripe &&
        paymentIntent.status === "requires_action" &&
        paymentIntent.clientSecret
      ) {
        try {
          stripe
            .confirmCardPayment(paymentIntent.clientSecret)
            .then((result) => {
              if (
                result &&
                !result.error &&
                result.paymentIntent &&
                result.paymentIntent.status === "succeeded"
              ) {
                handleUpdatePaymentMethodSuccess();
              } else {
                return handleUpdatePaymentMethodFailed({
                  message:
                    (result.error && result.error.message) ||
                    t("payment_auth_error"),
                });
              }
            });
        } catch (e) {
          console.warn(e);
          return handleUpdatePaymentMethodFailed();
        }
      }
    }
  };

  const onUpdateVatNumber = async (data: VatFormData) => {
    authCheckCallApi(updateVatNumber, {
      variables: {
        taxId: {
          type: data.vatType,
          value: data.vatNumber,
        },
      },
    });
  };

  const handleRequiredAction = (clientSecret: string) => {
    if (!stripe) {
      return handleUpdatePaymentMethodFailed();
    }

    try {
      stripe.confirmCardSetup(clientSecret).then((result) => {
        if (
          result &&
          !result.error &&
          result.setupIntent &&
          result.setupIntent.status === "succeeded"
        ) {
          handleUpdatePaymentMethodSuccess();
        } else {
          return handleUpdatePaymentMethodFailed({
            message:
              (result.error && result.error.message) || t("payment_auth_error"),
          });
        }
      });
    } catch (e) {
      console.warn(e);
      return handleUpdatePaymentMethodFailed();
    }
  };

  const handleUpdatePaymentMethodSuccess = () => {
    setUpdatePaymentMethod(false);
    setIsLoading(false);

    setMessage({
      title: t("update_pay_method"),
      message: t("update_pay_ok"),
    });

    setTimeout(() => {
      setSkipSubUpdate(false);
      authCheckCallApi(getStripeSubscription);
    }, 0);
  };

  const handleUpdatePaymentMethodFailed = (error?: any) => {
    setIsLoading(false);
    if (!message) {
      let messageText = t("update_pay_error");
      if (error) {
        const errorText = parseGraphQlError(error);
        messageText = `${messageText}
                       ${errorText}`;
      }

      setMessage({
        title: t("update_pay_method"),
        message: messageText,
      });

      setTimeout(() => {
        setSkipSubUpdate(false);
        authCheckCallApi(getStripeSubscription);
      }, 0);
    }
  };

  const handleUpdatePaymentMethod = (data: StripeSubscription | null) => {
    setUpdatePaymentMethod(false);
    setIsLoading(true);

    if (data && data.stripeSubscription && data.stripeSubscription.id) {
      const pendingSetupIntent = data.stripeSubscription.pendingSetupIntent;
      if (
        pendingSetupIntent &&
        pendingSetupIntent.status === "requires_action" &&
        pendingSetupIntent.clientSecret
      ) {
        return handleRequiredAction(pendingSetupIntent.clientSecret);
      }
    }

    handleUpdatePaymentMethodSuccess();
  };

  const [updateStripePaymentMethod] = useMutation(
    updateStripePaymentCardMutation,
    {
      onError: (e: any) => {
        handleUpdatePaymentMethodFailed(e);
      },
      onCompleted: (data: StripeSubscription) => {
        handleUpdatePaymentMethod(data);
      },
    }
  );

  const getDefaultCard = () => {
    return (
      subscriptionData &&
      subscriptionData.stripeSubscription &&
      subscriptionData.stripeSubscription.exists &&
      subscriptionData.stripeSubscription.defaultCard
    );
  };

  const getPaymentMethodDescription = (isError?: boolean) => {
    const defaultCard = getDefaultCard();
    if (!defaultCard) {
      return isError ? t("no_pay_info") : t("pay_method_reg");
    }

    return `•••• •••• •••• ${defaultCard.last4}`;
  };

  const getBrandIconName = () => {
    const defaultCard = getDefaultCard();

    if (defaultCard && defaultCard.brand) {
      const matchBrand = defaultCard.brand.toLocaleLowerCase();
      if (matchBrand.includes("visa")) {
        return "cc visa";
      }
      if (matchBrand.includes("american")) {
        return "cc amex";
      }
      if (matchBrand.includes("mastercard")) {
        return "cc mastercard";
      }
      if (matchBrand.includes("diners")) {
        return "cc diners club";
      }
      if (matchBrand.includes("jcb")) {
        return "cc jcb";
      }
      if (matchBrand.includes("discover")) {
        return "cc discover";
      }

      return "credit card";
    }

    return null;
  };

  const getInvoiceRow = (invoice: StripeInvoice) => {
    const total = invoice.total.toString();
    const invoiceTotal = new Intl.NumberFormat(navigator.language, {
      style: "currency",
      currency: invoice.currency,
    }).format(
      parseFloat(
        `${total.substring(0, total.length - 2)}.${total.substring(
          total.length - 2
        )}`
      )
    );

    const formatDate = (dateValue: any) =>
      dateValue
        ? new Date(dateValue).toLocaleDateString(navigator.language)
        : "";

    return (
      <Table.Row key={invoice.invoiceNumber}>
        <Table.Cell>{formatDate(invoice.created)}</Table.Cell>
        <Table.Cell>
          {formatDate(invoice.items[0].periodStart)} -{" "}
          {formatDate(invoice.items[0].periodEnd)}
        </Table.Cell>
        <Table.Cell>{getPlanInfo(invoice.items[0].plan).name}</Table.Cell>
        <Table.Cell>{invoice.status}</Table.Cell>
        <Table.Cell>{invoiceTotal}</Table.Cell>
        <Table.Cell textAlign="right">
          {invoice.hostedInvoiceUrl && (
            <a
              href={invoice.hostedInvoiceUrl}
              className={styles.myAccountLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Ico
                name="open_new"
                style={{ marginRight: "5px", verticalAlign: "middle" }}
                className={styles.ico}
              />
              {t("view")}
            </a>
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  const onSubmitPaymentMethod = async (
    billingDetails: PaymentMethodCreateParams.BillingDetails
  ) => {
    setIsLoading(true);
    const result = await createPaymentMethod(stripe, elements, billingDetails);
    if (result.message || !result.paymentMethod) {
      return handleUpdatePaymentMethodFailed(result);
    }

    authCheckCallApi(updateStripePaymentMethod, {
      variables: { paymentMethodId: result.paymentMethod.id },
    });
  };

  const paymentMethodIcon = getBrandIconName();

  const updatePaymentMethodPopup = () => {
    return (
      <Transition transitionOnMount animation="fade" duration="250">
        <Modal
          style={{ padding: "1rem" }}
          centered
          closeOnDimmerClick={false}
          closeOnEscape={false}
          size="tiny"
          open
          onClose={() => {
            setUpdatePaymentMethod(false);
          }}
        >
          <Dimmer active={isLoading} inverted>
            <Loader
              active={isLoading}
              indeterminate
              size="large"
              content="Please wait..."
            />
          </Dimmer>

          <div
            className={styles.sectionHeader}
            style={{ marginLeft: "1.5rem", marginTop: "1.5rem" }}
          >
            {t("update_pay_method")}
            <div
              className={cStyles.closeButton}
              style={{ top: "1.5rem", right: "1.5rem" }}
              onClick={() => {
                setSkipOrgUpdate(false);
                setUpdatePaymentMethod(false);
              }}
            >
              <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
            </div>
          </div>

          <Modal.Content style={{ fontSize: "16px" }}>
            <PaymentMethod
              onSubmitPaymentMethod={onSubmitPaymentMethod}
              onCancel={() => setUpdatePaymentMethod(false)}
              actionTitle={t("update_pay_method")}
            />
          </Modal.Content>
        </Modal>
      </Transition>
    );
  };

  const usageContents = () => {
    const appsProtected = usageData.usage.appsProtected.length;
    const periodEndDate = new Date(usageData.usage.periodEnd);
    const now = new Date();

    const periodDaysLeft =
      (periodEndDate.getTime() - now.getTime()) / (1000 * 3600 * 24);
    const periodDaysLeftStr = periodDaysLeft.toFixed(0);

    const showProtectedApps =
      ["protectStarter", "protectStarterSingleApp", "protectStandard"].includes(
        currentPlan
      ) || isMonitoringPlan(currentPlan);

    const billingPeriodApps = () => {
      return (
        <div>
          {usageData.usage.appsProtected.map((app: any) => {
            const appData = app.appId.split(":");
            return (
              <div key={app.appId} style={{ lineHeight: "30px" }}>
                <Ico
                  name={appData[1] === "android" ? "android" : "ios"}
                  style={{
                    verticalAlign: "middle",
                    marginRight: "10px",
                  }}
                />
                {appData[0]}
              </div>
            );
          })}
          <div
            style={{ borderTop: "1px solid #d9d9d9", margin: "10px 0" }}
          ></div>
          <div style={{ textAlign: "right" }}>
            {`Successful builds: ${usageData.usage.totalProtections}`}
            {["protectStarter", "protectStarterSingleApp"].includes(currentPlan)
              ? ` (out of ${planInfo.maxVers} available)`
              : ""}
          </div>
        </div>
      );
    };

    return (
      <Accordion style={{ width: "100%" }}>
        <Accordion.Title
          className={styles.advancedTitle}
          active={showUsage === true}
          index={1}
          onClick={() => {
            setShowUsage(!showUsage);
          }}
        >
          <Ico
            name={showUsage === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          {t("billing_period")}
        </Accordion.Title>
        <Accordion.Content active={showUsage === true}>
          <Grid stackable>
            <Grid.Row style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <Grid.Column
                width={4}
                verticalAlign="middle"
                style={{ marginLeft: "2rem", color: "#555" }}
              >
                {t("current_period")}
              </Grid.Column>

              {showProtectedApps && (
                <Grid.Column width={4}>
                  {appsProtected ? (
                    <Popup
                      wide
                      basic
                      position="bottom left"
                      mouseLeaveDelay={300}
                      content={billingPeriodApps()}
                      trigger={
                        <span className={styles.myAccountLink}>
                          <span>
                            {`${appsProtected} ${
                              appsProtected > 1
                                ? t("common:apps")
                                : t("common:app")
                            } ${t("protected_successfully")}`}
                            <Ico
                              name="help"
                              style={{
                                marginLeft: "5px",
                                verticalAlign: "text-bottom",
                              }}
                              className={styles.ico}
                            />
                          </span>
                        </span>
                      }
                    />
                  ) : (
                    t("no_apps_protected")
                  )}
                </Grid.Column>
              )}

              <Grid.Column width={4}>
                <Popup
                  basic
                  wide
                  mouseLeaveDelay={300}
                  position="bottom left"
                  content={
                    <div>
                      {t("billing_ends")}{" "}
                      {periodEndDate.toLocaleDateString(
                        navigator.language,
                        DateFormatLong
                      )}
                    </div>
                  }
                  trigger={
                    <span className={styles.myAccountLink}>
                      <span>
                        {`${periodDaysLeftStr} ${
                          periodDaysLeftStr === "1" ? "day" : "days"
                        } remaining`}
                        <Ico
                          name="cal"
                          style={{
                            marginLeft: "5px",
                            verticalAlign: "text-bottom",
                          }}
                          className={styles.ico}
                        />
                      </span>
                    </span>
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>
      </Accordion>
    );
  };

  const dimmerActive =
    loadingPaymentMethod ||
    loadingOrgInfo ||
    loadingInvoices ||
    updateVatLoading;

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={dimmerActive}>
              <Loader active={dimmerActive} size="large">
                {t("common:loading")}
              </Loader>
            </Dimmer>

            <div className={styles.sectionHeaderMargin}>
              {t("subscription_details")}
            </div>

            <Grid stackable>
              <Grid.Row verticalAlign="middle">
                <Grid.Column
                  width={4}
                  verticalAlign="middle"
                  className={styles.myAccountNoUsersLabel}
                >
                  {t("current_sub")}
                </Grid.Column>

                <Grid.Column width={4} verticalAlign="middle">
                  <div
                    className={styles.myAccountInfoLabel}
                    style={{ color: planInfo.color }}
                  >
                    {planInfo.name}
                  </div>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4} className={styles.myAccountNoUsersLabel}>
                  {t("payment_method")}
                </Grid.Column>

                <Grid.Column width={4}>
                  <label className={styles.paymentMethodDetails}>
                    {paymentMethodIcon && (
                      <Icon size="large" name={paymentMethodIcon} />
                    )}
                    {paymentMethod}
                  </label>
                </Grid.Column>

                {getDefaultCard() && !isPlanExpires && (
                  <Grid.Column width={4}>
                    <span
                      className={styles.myAccountLink}
                      onClick={() => {
                        setUpdatePaymentMethod(true);
                      }}
                    >
                      <Ico
                        name="pencil"
                        style={{
                          marginRight: "5px",
                          verticalAlign: "text-bottom",
                        }}
                        className={styles.ico}
                      />
                      {t("update_pay_method")}
                    </span>
                  </Grid.Column>
                )}
              </Grid.Row>

              {isPlanExpires && (
                <Grid.Row>
                  <Grid.Column
                    width={4}
                    className={styles.myAccountNoUsersLabel}
                  >
                    {t("subscription_active")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <label className={styles.paymentMethodDetails}>
                      {new Date(parseInt(planExpires)).toLocaleDateString(
                        navigator.language
                      )}
                    </label>
                  </Grid.Column>
                </Grid.Row>
              )}

              {usageData && usageData.usage && (
                <Grid.Row style={{ paddingBottom: 0 }}>
                  {usageContents()}
                </Grid.Row>
              )}

              {invoiceData && invoiceData.invoices.length > 0 && (
                <Grid.Row style={{ paddingBottom: 0 }}>
                  <Accordion style={{ width: "100%" }}>
                    <Accordion.Title
                      className={styles.advancedTitle}
                      active={showInvoices === true}
                      index={1}
                      onClick={() => {
                        if (showInvoices) {
                          return setShowInvoices(false);
                        }
                        setShowInvoices(true);
                      }}
                    >
                      <Ico
                        name={showInvoices === true ? "down" : "right"}
                        className={styles.accordionIcon}
                      />
                      {t("invoices")}
                    </Accordion.Title>
                    <Accordion.Content active={showInvoices === true}>
                      <div style={{ maxHeight: "20rem", overflow: "auto" }}>
                        {invoiceData && (
                          <Table
                            compact
                            basic="very"
                            style={{
                              paddingLeft: "2rem",
                              paddingRight: "2rem",
                            }}
                            stackable
                          >
                            <Table.Header>
                              <Table.Row className={styles.invoiceTableHeader}>
                                <Table.HeaderCell>
                                  {t("common:date")}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                  {t("billing_period")}
                                </Table.HeaderCell>
                                <Table.HeaderCell>{t("plan")}</Table.HeaderCell>
                                <Table.HeaderCell>
                                  {t("status")}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                  {t("total")}
                                </Table.HeaderCell>
                                <Table.HeaderCell></Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {invoiceData.invoices.map(
                                (invoice: StripeInvoice) =>
                                  getInvoiceRow(invoice)
                              )}
                            </Table.Body>
                          </Table>
                        )}
                      </div>
                    </Accordion.Content>
                  </Accordion>
                </Grid.Row>
              )}

              {getDefaultCard() && (
                <Grid.Row style={{ paddingBottom: 0 }}>
                  <Accordion style={{ width: "100%" }}>
                    <Accordion.Title
                      className={styles.advancedTitle}
                      active={showVat === true}
                      index={1}
                      onClick={() => {
                        if (showVat) {
                          return setShowVat(false);
                        }
                        setShowVat(true);
                      }}
                    >
                      <Ico
                        name={showVat === true ? "down" : "right"}
                        className={styles.accordionIcon}
                      />
                      {t("vat")}
                    </Accordion.Title>
                    <Accordion.Content active={showVat === true}>
                      <Form
                        onSubmit={handleSubmit(onUpdateVatNumber)}
                        style={{ padding: "1rem" }}
                      >
                        <Grid stackable>
                          <Grid.Row>
                            <Grid.Column
                              width={4}
                              className={styles.myAccountNoUsersLabel}
                              verticalAlign="middle"
                            >
                              {t("vat_number")}
                            </Grid.Column>

                            <Grid.Column width={9} verticalAlign="middle">
                              <Form.Field
                                inline
                                required
                                error={!!errors["vatNumber"]}
                              >
                                <Form.Group inline>
                                  <input
                                    autoComplete="off"
                                    className={`${styles.myAccountInput} ${
                                      vatError ? styles.vatError : ""
                                    }`}
                                    placeholder="VAT Number"
                                    name="vatNumber"
                                    key="vatNumber"
                                    ref={register({
                                      required: "VAT Number is required",
                                    })}
                                    defaultValue={vatNumber}
                                    onChange={handleVatNumberChange}
                                  />
                                </Form.Group>
                              </Form.Field>
                            </Grid.Column>
                          </Grid.Row>

                          <Grid.Row>
                            <Grid.Column
                              width={4}
                              className={styles.myAccountNoUsersLabel}
                              verticalAlign="middle"
                            >
                              {t("vat_number_type")}
                            </Grid.Column>

                            <Grid.Column width={9} verticalAlign="middle">
                              <Form.Field
                                inline
                                required
                                error={!!errors.vatType}
                              >
                                <Form.Group inline>
                                  <Dropdown
                                    className={styles.myAccountInput}
                                    name="vatType"
                                    key="vatType"
                                    search
                                    selection
                                    options={vatOptions}
                                    placeholder="Select Type"
                                    value={vatType}
                                    onChange={handleVatTypeChange}
                                    icon={
                                      <Ico
                                        name="down"
                                        className={styles.dropdownIcon}
                                      />
                                    }
                                  />
                                </Form.Group>
                              </Form.Field>
                            </Grid.Column>
                          </Grid.Row>

                          <Grid.Row>
                            <Grid.Column
                              width={10}
                              verticalAlign="middle"
                              textAlign="right"
                              floated="right"
                            >
                              <Button
                                type="button"
                                secondary
                                className={cStyles.buttonSecond}
                                onClick={() =>
                                  onUpdateVatNumber({
                                    vatNumber: "",
                                    vatType: "",
                                  })
                                }
                                disabled={!enableRemoveVat}
                              >
                                <Ico
                                  name="cancel"
                                  style={{ marginRight: "10px" }}
                                />
                                {t("common:clear")}
                              </Button>

                              <Button
                                style={{ marginLeft: "1rem" }}
                                type="submit"
                                primary
                                className={cStyles.buttonPrime}
                              >
                                <Ico
                                  name="check"
                                  style={{ marginRight: "10px" }}
                                />
                                {t("update_vat")}
                              </Button>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Form>
                    </Accordion.Content>
                  </Accordion>
                </Grid.Row>
              )}

              {enableSubscriptionChange && (
                <Grid.Row style={{ paddingBottom: 0 }}>
                  <Accordion style={{ width: "100%" }}>
                    <Accordion.Title
                      className={styles.advancedTitle}
                      active={showAdvanced === true}
                      index={1}
                      onClick={() => {
                        setShowAdvanced(!showAdvanced);
                      }}
                    >
                      <Ico
                        name={showAdvanced === true ? "down" : "right"}
                        className={styles.accordionIcon}
                      />
                      {t("common:advanced")}
                    </Accordion.Title>
                    <Accordion.Content active={showAdvanced === true}>
                      <Grid stackable>
                        <Grid.Row>
                          <Grid.Column
                            width={4}
                            verticalAlign="middle"
                            style={{ marginLeft: "2rem", color: "#555" }}
                          >
                            {t("cancellation")}
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <span
                              className={styles.myAccountLink}
                              onClick={() => {
                                setChangeSubscription("cancel");
                              }}
                            >
                              <Ico
                                name="cancel"
                                style={{
                                  marginRight: "5px",
                                  verticalAlign: "text-bottom",
                                }}
                                className={styles.ico}
                              />
                              {t("cancel_subscription")}
                            </span>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Accordion.Content>
                  </Accordion>
                </Grid.Row>
              )}

              {isPlanExpires && (
                <Grid.Row>
                  <Grid.Column stretched>
                    <div className={styles.buttonContainer}>
                      <Button
                        style={{ marginLeft: "1rem" }}
                        primary
                        className={cStyles.buttonPrime}
                        onClick={() => {
                          if (stripeEnabled) {
                            setChangeSubscription("resume");
                          } else {
                            setMessage({
                              title: t("resume_subscription"),
                              message: t("contact_resume"),
                            });
                          }
                        }}
                      >
                        <Ico name="check" style={{ marginRight: "10px" }} />
                        {t("resume_subscription")}
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </Segment>
        </Transition>
      </Container>
      {changeSubscription && (
        <ChangeSubscriptionDialog
          isCancel={changeSubscription === "cancel"}
          orgId={orgId}
          onCloseDialog={() => setChangeSubscription(null)}
        />
      )}

      {updatePaymentMethod && updatePaymentMethodPopup()}

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Payment;
